/**
 * Settings → Messaging's API (slice 2e spec §6, §11): the person's own links
 * (`GET /api/messaging`) and their changes. The `messaging` hub event
 * refetches `messagingKey` (`app/events.ts`), so the page and the link window
 * flip live when a code arrives.
 *
 * A code is shown once: only `POST /api/messaging/links` returns it (the
 * server keeps its hash). This tab holds it in memory, by person and link id
 * (`heldCodes`), so the row's Show code can open it again; after a reload it's
 * gone, and the row offers New code. Only the signed-in person's codes are
 * ever read, and the session's end (log out, or another sign-in) drops them.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "../../app/api";
import { useMe } from "../../app/me";
import { inSession, sessionMark, type SessionMark } from "../../app/session";
import { threadsKey } from "../chats/api";
import { clockSkew } from "../settings/integrations-view";
import { withLink, withLinked, withoutLink } from "./view";

export type MessagingAppId = "imessage";

export type LinkStatus = "pending" | "confirming" | "linked";

export type Link = {
  id: string;
  app: MessagingAppId;
  status: LinkStatus;
  /** What the person gave, in E.164. */
  number: string;
  /** Whatever Apple sent the code from (a number or an email), once it arrived. */
  handle: string | null;
  /** The pool number they text. */
  line: string;
  /** A pending code's (and a confirming question's) end; null once linked. */
  expires_at: string | null;
  thread_id: string | null;
  linked_at: string | null;
};

/** `POST …/links`' answer: the link with its code, shown once. */
export type MadeLink = Link & { code: string; sms_url: string };

export type MessagingApp = {
  app: MessagingAppId;
  name: string;
  /** The link in progress (a relink's too), else the linked one. */
  link: Link | null;
  /** The linked one, kept through a relink. */
  linked: Link | null;
};

/** An address of the person's that can email the crew (slice 2h §4). */
export type EmailLink = {
  id: string;
  app: "email";
  address: string;
  status: "pending" | "linked";
  /** Their login email, linked once it was confirmed. */
  auto: boolean;
  /** A pending code's end; null once linked. */
  expires_at: string | null;
  linked_at: string | null;
};

/** The crew's address and the person's own (slice 2h §8.1). */
export type EmailView = {
  address: { name: string; full: string; status: "setting_up" | "current" } | null;
  /** A Change still setting up: the name it goes to. */
  changing: { name: string; full: string } | null;
  kept: { name: string; full: string; until: string }[];
  links: EmailLink[];
  max: number;
  /** Missing on an older Worker: available. */
  available?: boolean;
};

export type Messaging = {
  available: boolean;
  apps: MessagingApp[];
  /** Slice 2h: missing on an older Worker. */
  email?: EmailView;
  /** The server's clock when it answered: a code's countdown runs on it. */
  now?: string;
  /** Not the API's: the server's clock less this browser's, as of the answer (`clockSkew`). */
  skew?: number;
};

export const messagingKey = ["messaging"] as const;

/** A code and the link that sends it: `sms:` for a text, `mailto:` for an email (slice 2h). */
type Held = { code: string; sms_url: string };

const held = new Map<string, Map<string, Held>>();

/** Codes this tab was given, by person, then link id: `{ code, sms_url }`. */
export const heldCodes = {
  get: (userId: string | undefined, linkId: string): Held | null =>
    (userId && held.get(userId)?.get(linkId)) || null,
  set(userId: string, linkId: string, code: Held) {
    const mine = held.get(userId) ?? new Map<string, Held>();
    held.set(userId, mine.set(linkId, code));
  },
  delete: (userId: string | undefined, linkId: string) =>
    userId && held.get(userId)?.delete(linkId),
  /** The session ended: no one's codes stay. */
  clear: () => held.clear(),
};

/** The signed-in person's code for a link, if this tab was given it. */
export function useHeldCode(linkId: string | null): Held | null {
  const userId = useMe().data?.user.id;
  return linkId ? heldCodes.get(userId, linkId) : null;
}

export function useMessaging() {
  return useQuery({
    queryKey: messagingKey,
    queryFn: async () => {
      const data = await api<{ ok: true } & Messaging>("GET", "/api/messaging");
      return { ...data, skew: clockSkew(data.now, Date.now()) };
    },
  });
}

/**
 * A Messaging change, scoped to the session it starts in: its request gets
 * the session's signal, and its cache and held-code writes happen only if
 * that session is still this tab's. A late answer after a logout, or after
 * another person signed in here, writes nothing.
 */
function scoped<T, V>(change: {
  mutationFn: (vars: V, signal: AbortSignal) => Promise<T>;
  onSuccess?: (out: T, vars: V) => void;
  onSettled?: () => void;
}) {
  return {
    onMutate: (): SessionMark => sessionMark(),
    mutationFn: (vars: V) => change.mutationFn(vars, sessionMark().signal),
    onSuccess: (out: T, vars: V, mark: SessionMark | undefined) => {
      if (inSession(mark)) change.onSuccess?.(out, vars);
    },
    onSettled: (_out: unknown, _error: unknown, _vars: unknown, mark: SessionMark | undefined) => {
      if (inSession(mark)) change.onSettled?.();
    },
  };
}

/**
 * Link (or relink, or New code): a pending link with its code, held in this
 * tab. The page's data gains the link at once (its code stays in memory only),
 * so the window shows the code before the refetch lands, or if it fails.
 */
export function createLinkOptions(client: QueryClient, userId: string | undefined) {
  return scoped({
    mutationFn: (input: { app: MessagingAppId; number: string }, signal) =>
      api<{ ok: true; link: MadeLink }>("POST", "/api/messaging/links", input, { signal }),
    onSuccess: ({ link }) => {
      // With no one signed in to hold it for, it isn't held: the row offers New code.
      if (userId) heldCodes.set(userId, link.id, { code: link.code, sms_url: link.sms_url });
      client.setQueryData<Messaging>(messagingKey, (data) => data && withLink(data, link));
      void client.invalidateQueries({ queryKey: messagingKey });
    },
  });
}

export function useCreateLink() {
  const client = useQueryClient();
  const userId = useMe().data?.user.id;
  return useMutation(createLinkOptions(client, userId));
}

/**
 * Yes, that's me (`200 { link }`: it's the linked one now) or No (`204`: it's
 * gone). Either is applied to the page's data at once, so a failed refetch
 * never leaves the question showing.
 */
export function confirmLinkOptions(client: QueryClient) {
  return scoped({
    mutationFn: ({ id, yes }: { id: string; yes: boolean }, signal) =>
      api<{ ok: true; link: Link } | null>(
        "POST",
        `/api/messaging/links/${id}/confirm`,
        { yes },
        { signal },
      ),
    onSuccess: (out, { id }) => {
      client.setQueryData<Messaging>(
        messagingKey,
        (data) => data && (out?.link ? withLinked(data, out.link) : withoutLink(data, id)),
      );
    },
    onSettled: () => invalidateLinked(client),
  });
}

export function useConfirmLink() {
  return useMutation(confirmLinkOptions(useQueryClient()));
}

/**
 * Cancel or Unlink: that link leaves the page's data at once (a relink's
 * Cancel leaves the linked one), so a failed refetch never shows it again.
 */
export function removeLinkOptions(client: QueryClient, userId: string | undefined) {
  return scoped({
    mutationFn: (id: string, signal) =>
      api("DELETE", `/api/messaging/links/${id}`, undefined, { signal }),
    onSuccess: (_, id) => {
      heldCodes.delete(userId, id);
      client.setQueryData<Messaging>(messagingKey, (data) => data && withoutLink(data, id));
    },
    onSettled: () => invalidateLinked(client),
  });
}

export function useRemoveLink() {
  const client = useQueryClient();
  return useMutation(removeLinkOptions(client, useMe().data?.user.id));
}

/** A link made or gone: the page, and the thread's header and the Chats list. */
function invalidateLinked(client: QueryClient) {
  void client.invalidateQueries({ queryKey: messagingKey });
  void client.invalidateQueries({ queryKey: threadsKey });
  void client.invalidateQueries({ queryKey: ["thread"] });
}

// ── Email (slice 2h §4, §8) ─────────────────────────────────────────────

/** `POST …/links { app: "email" }`'s answer: the link with its code and its `mailto:`. */
type MadeEmailLink = EmailLink & { code: string; mailto: string };

/**
 * Add address: a pending email link with its code, held in this tab as a
 * text's is (its `mailto:` as the link), in place of one in progress.
 */
export function useCreateEmailLink() {
  const client = useQueryClient();
  const userId = useMe().data?.user.id;
  return useMutation(
    scoped({
      mutationFn: (address: string, signal) =>
        api<{ ok: true; link: MadeEmailLink }>(
          "POST",
          "/api/messaging/links",
          { app: "email", address },
          { signal },
        ),
      onSuccess: ({ link }) => {
        if (userId) heldCodes.set(userId, link.id, { code: link.code, sms_url: link.mailto });
        const { code: _code, mailto: _mailto, ...made } = link;
        client.setQueryData<Messaging>(messagingKey, (data) =>
          data?.email
            ? {
                ...data,
                email: {
                  ...data.email,
                  links: [...data.email.links.filter((l) => l.status === "linked"), made],
                },
              }
            : data,
        );
        void client.invalidateQueries({ queryKey: messagingKey });
      },
    }),
  );
}

export type NameCheck = {
  available: boolean;
  reason?: "invalid" | "reserved" | "taken" | "current";
  suggestions: string[];
};

/** Change's live check (§8.2), the owner's, as they type: none for an empty name. */
export function useNameCheck(name: string) {
  return useQuery({
    queryKey: ["messaging", "email-name", name],
    enabled: name.length > 0,
    staleTime: 10_000,
    queryFn: ({ signal }) =>
      api<{ ok: true } & NameCheck>(
        "GET",
        `/api/messaging/email-address/check?name=${encodeURIComponent(name)}`,
        undefined,
        { signal },
      ),
  });
}

/** The owner's Change (§3, §8.2): the page's data takes the answer's `email`. */
export function useRenameAddress() {
  const client = useQueryClient();
  return useMutation(
    scoped({
      mutationFn: (name: string, signal) =>
        api<{ ok: true; email: Omit<EmailView, "links" | "max" | "available"> }>(
          "PUT",
          "/api/messaging/email-address",
          { name },
          { signal },
        ),
      onSuccess: ({ email }) => {
        client.setQueryData<Messaging>(messagingKey, (data) =>
          data?.email ? { ...data, email: { ...data.email, ...email } } : data,
        );
        void client.invalidateQueries({ queryKey: messagingKey });
      },
    }),
  );
}
